import { useNavigate, useParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { getRace } from '@character-creator/data';
import { useCharacterCreationStore } from '../../stores/characterCreationStore';
import { usePlaySessionStore } from '../../stores/playSessionStore';
import { useCharacterBenefits } from '../../hooks/useCharacterBenefits';
import { useCharacterReadiness } from '../../hooks/useCharacterReadiness';
import { downloadCharacterJson } from '../../utils/characterExport';
import { BenefitsSummary } from './BenefitsSummary';
import { ReadinessChecklist } from './ReadinessChecklist';
import { ChevronRightIcon, SaveIcon, PlayIcon } from '../ui/Icons';
import { PdfExportButton } from '../ui/PdfExportButton';

interface BenefitsPreviewPanelProps {
  isFullPanel?: boolean;
  showExportButtons?: boolean;
}

export function BenefitsPreviewPanel({ isFullPanel = false, showExportButtons = false }: BenefitsPreviewPanelProps) {
  const { isPreviewOpen, togglePreview, draft } = useCharacterCreationStore();
  const { getExportableRuntimeState } = usePlaySessionStore();
  const benefits = useCharacterBenefits();
  const readiness = useCharacterReadiness();
  const navigate = useNavigate();
  const { ruleset } = useParams<{ ruleset: string }>();

  const handlePlayMode = () => {
    if (ruleset) {
      navigate(`/${ruleset}/play`);
    }
  };

  const race = draft.raceId ? getRace(draft.raceId) : null;
  const subrace = race?.subraces?.find((s) => s.id === draft.subraceId);

  const displayName = draft.name.trim() || 'New Character';

  // Build class display string - handles multiclass
  let classDisplay: string;
  if (benefits.isCommoner) {
    classDisplay = 'Commoner';
  } else if (benefits.isMulticlass) {
    classDisplay = benefits.classLevels.map((cl) => `${cl.className} ${cl.level}`).join(' / ');
  } else if (benefits.classLevels.length > 0) {
    const cl = benefits.classLevels[0];
    classDisplay = cl.level > 1 ? `${cl.className} ${cl.level}` : cl.className;
  } else {
    classDisplay = '';
  }

  const displayRaceClass = [
    race?.name,
    subrace?.name ? `(${subrace.name})` : null,
    classDisplay,
  ]
    .filter(Boolean)
    .join(' ');

  const handleExportJson = () => {
    const runtimeState = getExportableRuntimeState();
    downloadCharacterJson(draft, runtimeState ?? undefined);
  };

  // Full panel mode (new layout)
  if (isFullPanel) {
    return (
      <div className="h-full flex flex-col">
        {/* Character Header with Export Buttons */}
        <div className="p-4 border-b border-border-default bg-background-elevated/50">
          <div className="flex items-center justify-between gap-2 mb-2">
            <h2 className="font-display text-xl font-bold text-accent-gold truncate">
              {displayName}
            </h2>
            {showExportButtons && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={handlePlayMode}
                  className="flex flex-col items-center px-3 py-1.5 rounded-lg text-text-secondary hover:text-success hover:bg-success/10 transition-colors"
                  title="Enter Play Mode"
                >
                  <PlayIcon className="w-5 h-5" />
                  <span className="text-[10px] font-medium mt-0.5">PLAY</span>
                </button>
                <PdfExportButton
                  variant="icon"
                  characterName={displayName}
                />
                <button
                  type="button"
                  onClick={handleExportJson}
                  className="flex flex-col items-center px-3 py-1.5 rounded-lg text-text-secondary hover:text-accent-gold hover:bg-background-secondary transition-colors"
                  title="Save character as a JSON file that you can import later to restore your character"
                >
                  <SaveIcon className="w-5 h-5" />
                  <span className="text-[10px] font-medium mt-0.5">JSON</span>
                </button>
              </div>
            )}

          </div>
          {displayRaceClass && (
            <p className="text-sm text-text-secondary">{displayRaceClass}</p>
          )}
        </div>

        {/* Readiness Checklist */}
        <ReadinessChecklist readiness={readiness} />

        {/* Benefits Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <BenefitsSummary benefits={benefits} />
        </div>
      </div>
    );
  }

  // Legacy collapsible mode (for backwards compatibility)
  return (
    <aside
      className={clsx(
        'flex-shrink-0 bg-background-card border-l border-border-default h-full transition-all duration-300 overflow-hidden',
        isPreviewOpen ? 'w-80' : 'w-12'
      )}
    >
      {/* Toggle Button */}
      <button
        type="button"
        onClick={togglePreview}
        className="w-full h-12 flex items-center justify-center border-b border-border-default hover:bg-background-elevated transition-colors"
        aria-label={isPreviewOpen ? 'Collapse preview' : 'Expand preview'}
      >
        <ChevronRightIcon
          className={clsx(
            'w-5 h-5 text-text-muted transition-transform',
            isPreviewOpen ? 'rotate-0' : 'rotate-180'
          )}
        />
      </button>

      {/* Preview Content */}
      {isPreviewOpen && (
        <div className="p-4 overflow-y-auto h-[calc(100%-3rem)]">
          <h3 className="font-display text-lg font-bold text-text-primary mb-4">
            Character Preview
          </h3>
          <BenefitsSummary benefits={benefits} />
        </div>
      )}

      {/* Collapsed state - vertical label */}
      {!isPreviewOpen && (
        <div className="flex items-center justify-center h-32">
          <span
            className="text-xs text-text-muted font-medium tracking-wider"
            style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
          >
            PREVIEW
          </span>
        </div>
      )}
    </aside>
  );
}
